/**
 * useDock — keep the guide out of the way of whatever is already on screen.
 *
 * The panel lives bottom-right, which is exactly where this app puts the things
 * a user needs during a guided task: every side drawer slides in from the right
 * with its submit button in the footer, so a fixed bottom-right panel sits on top
 * of "Add Resident" / "Relocate" / "Add & Assign".
 *
 * So when a right-anchored drawer is open, the panel steps left to sit beside it
 * rather than over it. Centred dialogs (`.pp-dialog`) are left alone — they don't
 * reach the bottom-right corner, and shifting for them would be movement for no
 * benefit.
 *
 * Returns a style object to spread onto the panel. `null` means "use the CSS
 * default".
 */

import { useState, useEffect } from 'react';
import { isOnScreen } from './useAnchor.js';

/* Right-anchored drawers, by page prefix. All three share the same pattern:
   position:fixed; right:0, translated off-screen when closed, `.is-open` when
   shown. The shared v2 drawers (EasyAddDeviceDrawer) render with an rp-/ap-
   prefix, so they're covered by these. */
const DRAWER_SELECTORS = [
    '.rp-drawer.is-open',
    '.ap-drawer.is-open',
    '.dp-drawer.is-open',
    '.up-drawer.is-open',
];

/** Gap between the panel and the drawer edge it's dodging. */
const GAP = 16;

/** Drawers animate over ~300ms, so re-measure while a flow is active. */
const POLL_MS = 200;

function measure() {
    let leftmost = null;
    DRAWER_SELECTORS.forEach((sel) => {
        let nodes;
        try {
            nodes = document.querySelectorAll(sel);
        } catch {
            return;
        }
        nodes.forEach((el) => {
            if (!isOnScreen(el)) return;
            const r = el.getBoundingClientRect();
            // Only care about surfaces actually occupying the right side.
            if (r.right < window.innerWidth - 40) return;
            if (leftmost === null || r.left < leftmost) leftmost = r.left;
        });
    });
    return leftmost;
}

/**
 * @param {boolean} active — false disables polling entirely
 * @returns {object|null} style overrides for the panel, or null for the default
 */
export default function useDock(active) {
    const [dockStyle, setDockStyle] = useState(null);

    useEffect(() => {
        if (!active) {
            setDockStyle(null);
            return undefined;
        }

        const apply = () => {
            const drawerLeft = measure();
            if (drawerLeft === null) {
                setDockStyle((prev) => (prev === null ? prev : null));
                return;
            }
            /* Sit to the left of the drawer. Clamped so a very wide drawer can't
               push the panel off the left edge — at that point overlapping is the
               lesser evil, and the panel can still be collapsed. */
            const right = Math.min(
                Math.max(window.innerWidth - drawerLeft + GAP, 0),
                Math.max(window.innerWidth - 360, 0),
            );
            setDockStyle((prev) => (prev && prev.right === `${right}px` ? prev : { right: `${right}px` }));
        };

        apply();
        const poll = setInterval(apply, POLL_MS);
        window.addEventListener('resize', apply);
        return () => {
            clearInterval(poll);
            window.removeEventListener('resize', apply);
        };
    }, [active]);

    return dockStyle;
}
